import { cmxURL } from 'endpoints';
import { setLanguageBundle, updateLanguageBundle } from '@nackle/intl-tools';
import { GOLD_COMPANY_ID } from '../constants';

const DEFAULT_LOCALE = 'en-US';

function CMXService( fetch ) {
    async function getBundle( bundleName, locales, companyId, returnStringAttributes = true ) {
        const headers = companyId ?
            {
                'content-type': 'application/json',
                'company-id': companyId
            } :
            {
                'content-type': 'application/json'
            };
        const res = await fetch(
            `${ cmxURL }/bundle/${ encodeURIComponent( bundleName ) }`,
            {
                method: 'POST',
                headers: headers,
                body: JSON.stringify( { locales, returnStringAttributes: returnStringAttributes } )
            }
        );
        const data = await res.json();
        if ( res.ok ) {
            return data;
        } else {
            throw data;
        }
    }

    async function getBundleByAttribute( bundleName, locales, companyId, getGold ) {
        const attributeValue = getGold ? GOLD_COMPANY_ID : companyId;
        const res = await fetch(
            `${ cmxURL }/bundle/${ encodeURIComponent( bundleName ) }/byAttribute/companyId`,
            {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    'company-id': getGold ? GOLD_COMPANY_ID : companyId
                },
                body: JSON.stringify( { locales, attributeValue: attributeValue } )
            }
        );

        const data = await res.json();
        if ( res.ok ) {
            return data;
        } else {
            throw data;
        }
    }

    async function getBundleWithAttributes( bundleName, locale ) {
        const res = await fetch(
            `${ cmxURL }/bundle/${ encodeURIComponent( bundleName ) }`,
            {
                method: 'POST',
                headers: {
                    'content-type': 'application/json'
                },
                body: JSON.stringify( { locales: [ locale ], returnStringAttributes: true } )
            }
        );

        const data = await res.json();

        if ( res.ok ) {
            return data;
        } else {
            throw data;
        }
    }

    async function loadBundles( bundleNames, locale = DEFAULT_LOCALE, companyId ) {
        // fetch and compile the bundles for nav studio
        const bundleData = await Promise.all( bundleNames.map( async bundleName => {
            if ( bundleName === 'navigation' ) {

                // fetch regular and attribute bundle
                const cmp = companyId ? companyId : GOLD_COMPANY_ID;
                const bundle = await getBundleByAttribute( bundleName, [ locale ], cmp );
                const regBundle = await getBundle( bundleName, [ locale ], cmp, false );

                //if we have the bundle from the fetch, compile bundle
                if ( bundle.bundles[ locale ] ) {
                    Object.keys( bundle.bundles[ locale ] ).forEach( k => {
                        // make it a string instead of an object
                        if ( typeof bundle.bundles[ locale ][ k ] !== 'string' ) {
                            bundle.bundles[ locale ][ k ] = bundle.bundles[ locale ][ k ][ 0 ].value;
                        }
                    } );

                    // for any missing key/strings plop the regular string in there
                    Object.keys( regBundle.bundles[ locale ] ).forEach( k => {
                        if ( !bundle.bundles[ locale ][ k ] ) {
                            bundle.bundles[ locale ][ k ] = regBundle.bundles[ locale ][ k ];
                        }
                    } );

                }

                /**
                 * if we're fetching non gold, and there are gaps in the company string
                 * fill in the missing strings with gold
                 */
                if ( companyId !== GOLD_COMPANY_ID ) {
                    // get both gold bundles
                    const goldBundle = await getBundleByAttribute( bundleName, [ locale ], companyId, true );
                    const regGoldBundle = await getBundle( bundleName, [ locale ], companyId, false );

                    /**
                     * If we have the bundle, go about compiling the bundles
                     * fill in non-attribute strings where needed
                     */
                    if ( goldBundle.bundles[ locale ] ) {
                        // boil down key - objects into key - strings
                        Object.keys( goldBundle.bundles[ locale ] ).forEach( k => {
                            if ( typeof bundle.bundles[ locale ][ k ] !== 'string' ) {
                                goldBundle.bundles[ locale ][ k ] = goldBundle.bundles[ locale ][ k ][ 0 ].value;
                            }
                        } );
                        // fill in blanks with gold non-attribute strings
                        Object.keys( regGoldBundle.bundles[ locale ] ).forEach( k => {
                            if ( !goldBundle.bundles[ locale ][ k ] ) {
                                goldBundle.bundles[ locale ][ k ] = regBundle.bundles[ locale ][ k ];
                            }
                        } );

                        // fill would be company bundle with gold bundle options
                        Object.keys( goldBundle.bundles[ locale ] ).forEach( k => {
                            if ( !bundle.bundles[ locale ][ k ] ) {
                                bundle.bundles[ locale ][ k ] = goldBundle.bundles[ locale ][ k ];
                            }
                        } );
                    }
                }
                return bundle;
            } else {
                return getBundle( bundleName, [ locale ] );
            }
    } ) );

        let mergedBundle = bundleData[ 0 ].bundles[ locale ];
        for ( let i = 1; i < bundleData.length; i++ ) {
            mergedBundle = {
                ...mergedBundle,
                ...bundleData[ i ].bundles[ locale ]
            };
        }

        await setLanguageBundle( locale, mergedBundle );
        return mergedBundle;
    }

    const loadBundWithAttributes = async( bundleName ) => {
        const response = await getBundleWithAttributes( bundleName, DEFAULT_LOCALE );
        await updateLanguageBundle( response.bundles[ DEFAULT_LOCALE ] );
    };

    return {
        loadBundles,
        loadBundWithAttributes
    };
}

export default CMXService;
